/**
 * Card Collection Experience Module
 *
 * This module handles the complete card collection functionality including:
 * - Fetching and displaying card data in a grid layout
 * - Processing code redemptions
 * - Displaying card details in a modal
 */
(function () {
    'use strict';

    // CONSTANTS & CONFIGURATION
    const MESSAGE_TYPE_COLORS = {
        success: 'green',
        warning: 'orange',
        error: 'red'
    };

    /** Default fallback image when card images fail to load */
    const FALLBACK_IMAGE_URL = 'https://xcldpqvrbwoiygcfrvtq.supabase.co/storage/v1/object/public/card-collection/No_Image_Available.jpg';

    /** Defines the display order for card rarities (lowest index = shown first) */
    const RARITY_DISPLAY_ORDER = ['rare', 'super_rare', 'super_secret_rare', 'promo_rare', 'ultra_rare'];

    /** Event date range for special highlighting (format: YYYY-MM-DD) */
    const EVENT_DATE_RANGE = {
        start: '2026-01-13',
        end: '2026-01-16'
    };

    // UTILITY FUNCTIONS
    function safeParseJSON(jsonString, fallbackValue) {
        if (!jsonString) {
            return fallbackValue;
        }

        try {
            return JSON.parse(jsonString);
        } catch (parseError) {
            console.warn('JSON parsing failed, using fallback value:', parseError);
            return fallbackValue;
        }
    }

    /**
     * Checks if localStorage is available and functional
     * @returns {boolean} True if localStorage can be used
     */
    function isLocalStorageAvailable() {
        try {
            const testKey = '__storage_availability_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            return true;
        } catch (error) {
            return false;
        }
    }

    // DATA TRANSFORMATION FUNCTIONS
    function groupCardsBySeries(cards) {
        return cards.reduce((seriesGroups, card) => {
            const seriesName = card.series || 'Unknown';

            if (!seriesGroups[seriesName]) {
                seriesGroups[seriesName] = [];
            }

            seriesGroups[seriesName].push(card);
            return seriesGroups;
        }, {});
    }

    /**
     * Sorts cards with two priorities:
     * 1. Collected cards first (sorted by rarity, then alphabetically)
     * 2. Uncollected cards second (sorted by rarity, then alphabetically)
     */
    function sortCardsByCollectionStatus(cards, cardCopyCounts = {}) {
        const rarityRankMap = new Map(
            RARITY_DISPLAY_ORDER.map((rarity, index) => [rarity, index])
        );

        function getRarityRank(card) {
            const rarityKey = String(card.rarity || '').toLowerCase();
            return rarityRankMap.has(rarityKey)
                ? rarityRankMap.get(rarityKey)
                : Number.POSITIVE_INFINITY;
        }

        function isCardCollected(card) {
            const copyCount = cardCopyCounts[String(card.id)] || 0;
            return copyCount > 0;
        }

        return [...cards].sort((cardA, cardB) => {
            const isCollectedA = isCardCollected(cardA);
            const isCollectedB = isCardCollected(cardB);

            if (isCollectedA !== isCollectedB) {
                return isCollectedA ? -1 : 1;
            }

            const rarityRankA = getRarityRank(cardA);
            const rarityRankB = getRarityRank(cardB);

            if (rarityRankA !== rarityRankB) {
                return rarityRankA - rarityRankB;
            }

            return String(cardA.name || '').localeCompare(String(cardB.name || ''));
        });
    }

    function groupRedeemedCodesByCardId(redeemedCodes) {
        if (!Array.isArray(redeemedCodes)) {
            console.warn('[Card Collection] groupRedeemedCodesByCardId received non-array:', redeemedCodes);
            return {};
        }

        return redeemedCodes.reduce((codeGroups, codeEntry) => {
            if (!codeEntry || codeEntry.card_id == null) {
                return codeGroups;
            }

            const cardId = String(codeEntry.card_id);

            if (!codeGroups[cardId]) {
                codeGroups[cardId] = [];
            }

            codeGroups[cardId].push(codeEntry);
            return codeGroups;
        }, {});
    }

    function buildCardCopyCounts(progressEntries) {
        return progressEntries.reduce((counts, entry) => {
            const cardId = String(entry.card_id);
            counts[cardId] = (counts[cardId] || 0) + 1;
            return counts;
        }, {});
    }

    // MAIN APPLICATION INITIALIZATION
    document.addEventListener('DOMContentLoaded', function () {
        const collectionContainer = document.getElementById('card-collection');

        if (!collectionContainer) {
            console.warn('Card collection container not found');
            return;
        }

        const customerId = collectionContainer.dataset.customerId;
        const customerEmail = collectionContainer.dataset.customerEmail || null;
        const customerFirstName = collectionContainer.dataset.customerFirstName || null;
        const customerWhatsapp = collectionContainer.dataset.customerWhatsapp || null;
        const customerProvince = collectionContainer.dataset.customerProvince || null;
        const apiBaseUrl = (collectionContainer.dataset.apiBase || 'https://xcldpqvrbwoiygcfrvtq.supabase.co/functions/v1/card-collection')
            .replace(/\/$/, '');
        const vercelBypassToken = collectionContainer.dataset.vercelBypassToken || '';
        const pendingRedeemSessionKey = 'cv_pending_card_redeem';
        const urlRedeemCode = new URLSearchParams(window.location.search).get('redeem');

        function readPendingRedeemCode() {
            try {
                return window.sessionStorage.getItem(pendingRedeemSessionKey) || '';
            } catch (error) {
                return '';
            }
        }

        function savePendingRedeemCode(code) {
            if (!code) return;
            try {
                window.sessionStorage.setItem(pendingRedeemSessionKey, code);
            } catch (error) {
                // Continue with the URL value when session storage is unavailable.
            }
        }

        function clearPendingRedeemCode(code) {
            try {
                if (!code || window.sessionStorage.getItem(pendingRedeemSessionKey) === code) {
                    window.sessionStorage.removeItem(pendingRedeemSessionKey);
                }
            } catch (error) {
                // Nothing else is required when session storage is unavailable.
            }

            const currentUrl = new URL(window.location.href);
            if (!code || currentUrl.searchParams.get('redeem') === code) {
                currentUrl.searchParams.delete('redeem');
                window.history.replaceState({}, '', currentUrl.toString());
            }
        }

        if (urlRedeemCode) savePendingRedeemCode(urlRedeemCode);

        if (!customerId) {
            collectionContainer.innerHTML = `
                <p style="text-align:center">
                    <a href="/pages/register" style="color:blue;text-decoration:underline">Register</a> or 
                    <a href="/customer_authentication/login?return_to=%2Fpages%2Fcard-collection" style="color:blue;text-decoration:underline">Login</a> 
                    to see your card collection.
                </p>
            `;
            return;
        }

        const cardModal = document.getElementById('card-modal');
        const modalContentArea = document.getElementById('modal-content-details');
        const modalCloseButton = document.querySelector('.close-btn');
        const codeInputField = document.getElementById('card-code');
        const redeemButton = document.getElementById('redeem-btn');
        const redeemMessageDisplay = document.getElementById('redeem-msg');
        const seriesIconsContainer = document.getElementById('series-icons');

        const applicationState = {
            allCards: [],
            collectionProgress: [],
            cardsBySeriesName: {},
            cardCopyCounts: {},
            redeemedCodesByCardId: {},
            seriesCovers: {},
            series: []
        };

        function showStatusMessage(messageText, messageType = 'success') {
            redeemMessageDisplay.textContent = messageText || '';
            redeemMessageDisplay.style.color = MESSAGE_TYPE_COLORS[messageType] || '';
        }

        // Supabase Public Storage Base URL for your project
        const SUPABASE_STORAGE_PREFIX = 'https://xcldpqvrbwoiygcfrvtq.supabase.co/storage/v1/object/public/card-collection/';

        function getValidImageUrl(imageUrl) {
            if (imageUrl && imageUrl.trim() !== '') {
                const cleanUrl = imageUrl.trim();
                
                // If it is already a full URL (starts with http), return as is
                if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
                    return cleanUrl;
                }
                // Otherwise, prepend the Supabase public storage bucket path
                return `${SUPABASE_STORAGE_PREFIX}${cleanUrl}`;
            }
            return FALLBACK_IMAGE_URL;
        }

        function formatRarityLabel(rarity) {
            return String(rarity || '').replace(/_/g, ' ');
        }

        function formatDate(dateString) {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return '';
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            } catch (error) {
                return '';
            }
        }

        function isDateInEventRange(dateString) {
            if (!dateString || !EVENT_DATE_RANGE.start || !EVENT_DATE_RANGE.end) return false;
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return false;
                const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                const startDate = new Date(EVENT_DATE_RANGE.start);
                const endDate = new Date(EVENT_DATE_RANGE.end);
                endDate.setHours(23, 59, 59, 999);
                return checkDate >= startDate && checkDate <= endDate;
            } catch (error) {
                return false;
            }
        }

        function renderSkeletonLoader(cardCount = 6) {
            const skeletonCards = Array(cardCount)
                .fill('')
                .map(() => `
                    <div class="skeleton-card">
                        <div class="skeleton-img skeleton-pulse"></div>
                        <div class="skeleton-info">
                            <div class="skeleton-line skeleton-pulse" style="width: 80%"></div>
                            <div class="skeleton-line skeleton-pulse" style="width: 60%"></div>
                            <div class="skeleton-line skeleton-pulse" style="width: 40%"></div>
                        </div>
                    </div>
                `)
                .join('');

            return `
                <div class="skeleton-loader">
                    <div class="skeleton-header">
                        <div class="skeleton-title skeleton-pulse"></div>
                    </div>
                    <div class="skeleton-grid">
                        ${skeletonCards}
                    </div>
                </div>
            `;
        }

        // API CLIENT
        const requestCache = new Map();
        const CACHE_TTL = 30000; // 30 seconds

        async function makeApiRequest(path, options = {}) {
            const fullUrl = path.startsWith('http') ? path : `${apiBaseUrl}${path}`;
            const method = String(options.method || 'GET').toUpperCase();
            const isGetRequest = method === 'GET';
            const isCardsEndpoint = /\/cards(\?|$)/.test(fullUrl);

            const cacheKey = (isGetRequest && !isCardsEndpoint)
                ? `${fullUrl}_${JSON.stringify(options)}`
                : null;

            if (cacheKey) {
                const cached = requestCache.get(cacheKey);
                if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
                    return cached.data;
                }
            }

            const headers = { ...options.headers };
            if (options.body && !headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }
            if (vercelBypassToken && !headers['x-vercel-protection-bypass']) {
                headers['x-vercel-protection-bypass'] = vercelBypassToken;
            }

            const fetchOptions = { ...options, method, headers };
            const response = await fetch(fullUrl, fetchOptions);

            let responseData = null;
            try {
                responseData = await response.json();
            } catch (parseError) {
                responseData = null;
            }

            if (!response.ok) {
                const error = new Error(responseData?.message || responseData?.error || 'Request failed');
                error.response = responseData;
                error.status = response.status;
                throw error;
            }

            if (cacheKey && response.ok) {
                requestCache.set(cacheKey, { data: responseData, timestamp: Date.now() });
            }

            return responseData;
        }

        const api = {
            loadCardCollection(customerId) {
                return makeApiRequest(`/cards?customerId=${encodeURIComponent(customerId)}`);
            },
            redeemCode(payload) {
                return makeApiRequest('/redeem', { method: 'POST', body: JSON.stringify(payload) });
            },
            unbindCode(payload) {
                return makeApiRequest('/unbind', { method: 'POST', body: JSON.stringify(payload) });
            }
        };

        // CARD RENDERING FUNCTIONS
        function renderCardTile(card) {
            const copyCount = applicationState.cardCopyCounts[String(card.id)] || 0;
            const isCollected = copyCount > 0;
            const rarityClassName = `rarity-${String(card.rarity || '').toLowerCase()}`;
            const rarityLabel = formatRarityLabel(card.rarity);
            const imageUrl = getValidImageUrl(card.image_url);

            const collectionStatus = isCollected
                ? `<span>✅ (${copyCount}) Collected</span>`
                : '<span class="locked">🔒 Not Collected</span>';

            return `
                <div class="card ${isCollected ? 'collected' : 'uncollected'}"
                     data-card-id="${card.id}"
                     tabindex="0"
                     role="button">
                    <img class="card-img"
                         src="${imageUrl}"
                         alt="${card.name}"
                         loading="lazy"
                         onerror="this.onerror=null; this.src='${FALLBACK_IMAGE_URL}'" />
                    <div class="card-info">
                        <p class="card-name">${card.name}</p>
                        <p class="card-rarity ${rarityClassName}">${rarityLabel}</p>
                        ${collectionStatus}
                    </div>
                </div>
            `;
        }

        function getSortedSeriesNames() {
            const backendSortedNames = (applicationState.series || []).map(s => s.name);
            const currentNames = Object.keys(applicationState.cardsBySeriesName);
            
            // Start with backend order, only including those that have cards
            const sorted = backendSortedNames.filter(name => currentNames.includes(name));
            
            // Add any series found in cards but not in the backend series list (fallback)
            const remaining = currentNames.filter(name => !backendSortedNames.includes(name)).sort();
            
            return [...sorted, ...remaining];
        }

        function renderSeriesFilterIcons(cardsBySeriesName) {
            const sortedSeriesNames = getSortedSeriesNames();

            const iconElements = sortedSeriesNames
                .map(seriesName => {
                    const seriesCards = cardsBySeriesName[seriesName];
                    const coverImage = applicationState.seriesCovers[seriesName];
                    const thumbnailUrl = coverImage
                        ? getValidImageUrl(coverImage)
                        : getValidImageUrl(seriesCards[0]?.image_url);

                    return `
                        <div class="series-icon" data-series="${seriesName}" tabindex="0" role="button">
                            <img src="${thumbnailUrl}" alt="${seriesName}" loading="lazy" />
                            <span>${seriesName}</span>
                        </div>
                    `;
                })
                .join('');

            seriesIconsContainer.innerHTML = iconElements;
        }

        function renderAllSeriesView(cardsBySeriesName) {
            const sortedSeriesNames = getSortedSeriesNames();
            const seriesBlockElements = sortedSeriesNames
                .map(seriesName => {
                    const seriesCards = cardsBySeriesName[seriesName];
                    const uniqueCardsCollected = seriesCards.filter(
                        card => (applicationState.cardCopyCounts[String(card.id)] || 0) > 0
                    ).length;
                    const totalCardsInSeries = seriesCards.length;
                    const isSeriesComplete = uniqueCardsCollected === totalCardsInSeries;
                    const completionIndicator = isSeriesComplete ? '✅ Completed' : '';

                    const previewCards = seriesCards.slice(0, 5);
                    const hasMoreCards = seriesCards.length > 5;
                    const seeAllCardHtml = hasMoreCards
                        ? `<div class="card more-card" data-series="${seriesName}"><span>+ See All</span></div>`
                        : '';

                    return `
                        <div class="series-block" data-series="${seriesName}">
                            <div class="series-header">
                                <h2>${seriesName} (${uniqueCardsCollected}/${totalCardsInSeries}) ${completionIndicator}</h2>
                            </div>
                            <div class="series-grid">
                                ${previewCards.map(renderCardTile).join('')}
                                ${seeAllCardHtml}
                            </div>
                        </div>
                    `;
                })
                .join('');

            collectionContainer.innerHTML = seriesBlockElements;
        }

        function renderSingleSeriesView(seriesName) {
            const seriesCards = applicationState.cardsBySeriesName[seriesName] || [];
            const uniqueCardsCollected = seriesCards.filter(
                card => (applicationState.cardCopyCounts[String(card.id)] || 0) > 0
            ).length;
            const totalCardsInSeries = seriesCards.length;
            const completionIndicator = uniqueCardsCollected === totalCardsInSeries ? '✅ Completed' : '';

            collectionContainer.innerHTML = `
                <div class="series-block">
                    <div class="series-header">
                        <h2>${seriesName} (${uniqueCardsCollected}/${totalCardsInSeries}) ${completionIndicator}</h2>
                    </div>
                    <div class="series-grid">
                        ${seriesCards.map(renderCardTile).join('')}
                    </div>
                    <div class="series-buttons">
                        <button id="back-btn">⬅ Back to All Series</button>
                    </div>
                </div>
            `;
        }

        function refreshCollectionDisplay() {
            const urlParams = new URLSearchParams(window.location.search);
            const selectedSeriesName = urlParams.get('series');

            if (selectedSeriesName && applicationState.cardsBySeriesName[selectedSeriesName]) {
                renderSingleSeriesView(selectedSeriesName);
            } else {
                renderAllSeriesView(applicationState.cardsBySeriesName);
            }
            renderSeriesFilterIcons(applicationState.cardsBySeriesName);
        }

        // MODAL FUNCTIONS
        function renderModalCodeList(codes, copyCount) {
            const codesArray = Array.isArray(codes) ? codes : [];

            if (codesArray.length === 0) {
                const emptyMessage = copyCount > 0
                    ? 'No redeemed card found for this card.'
                    : "You haven't collected this card yet. Redeem a code to add it to your collection.";
                return `<p style="text-align: center;">${emptyMessage}</p>`;
            }

            const codeItemElements = codesArray
                .map(code => {
                    const redeemedDate = formatDate(code.redeemed_at);
                    const isInEventRange = isDateInEventRange(code.redeemed_at);
                    const eventClass = isInEventRange ? ' event-date' : '';
                    const dateDisplay = redeemedDate ? `<span class="redeemed-date${eventClass}">Redeemed: ${redeemedDate}</span>` : '';

                    return `
                        <div class="modal-code-item" data-code-id="${code.id}" data-code="${code.code}">
                            <div class="code-info">
                                <span class="code-value${eventClass}" style="font-family:monospace;">Code: ${code.code}</span>
                                ${dateDisplay}
                            </div>
                            <button class="unbind-btn-modal">Unbind</button>
                        </div>
                    `;
                })
                .join('');

            return `<div class="modal-code-list" id="modal-code-list-content">${codeItemElements}</div>`;
        }

        function openCardModal(cardData, redeemedCodes, copyCount) {
            if (!cardData) return;
            const imageUrl = getValidImageUrl(cardData.image_url);
            const rarityClassName = `rarity-${String(cardData.rarity || '').toLowerCase()}`;
            const rarityLabel = formatRarityLabel(cardData.rarity);

            const eligibleCodeCount = redeemedCodes ? redeemedCodes.filter(code => isDateInEventRange(code.redeemed_at)).length : 0;
            let eligibleSection = '';
            if (eligibleCodeCount > 0) {
                eligibleSection = `
                    <p>Eligible Code: <strong>${eligibleCodeCount}</strong></p>
                    <p>
                        <a href="https://coolvita.id/pages/lottery" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: underline;">
                            Announcement Date 27 February 2026
                        </a>
                    </p>
                `;
            }

            modalContentArea.innerHTML = `
                <div class="modal-header-detail">
                    <img class="modal-card-img" src="${imageUrl}" alt="${cardData.name}" />
                    <div class="modal-card-info">
                        <h3>${cardData.name}</h3>
                        <p>Rarity: <span class="card-rarity ${rarityClassName}">${rarityLabel}</span></p>
                        <p>Quantity: <strong>${copyCount}</strong></p>
                        ${eligibleSection}
                    </div>
                </div>
                ${renderModalCodeList(redeemedCodes, copyCount)}
            `;
            cardModal.style.display = 'block';
        }

        function closeCardModal() {
            cardModal.style.display = 'none';
            modalContentArea.innerHTML = '';
        }

        // DATA LOADING & REDEMPTION
        async function loadCardCollectionData({ showLoadingIndicator = true, forceRefresh = false, retryOnEmptyCodes = true } = {}) {
            if (showLoadingIndicator) collectionContainer.innerHTML = renderSkeletonLoader(6);
            if (forceRefresh) requestCache.clear();

            const responseData = await api.loadCardCollection(customerId);
            const rawCards = Array.isArray(responseData.cards) ? responseData.cards : [];
            const rawProgress = Array.isArray(responseData.progress) ? responseData.progress : [];
            const rawRedeemedCodes = Array.isArray(responseData.redeemedCodes) ? responseData.redeemedCodes : [];
            const rawSeriesCovers = responseData.seriesCovers || {};
            const rawSeries = Array.isArray(responseData.series) ? responseData.series : [];

            if (retryOnEmptyCodes && rawProgress.length > 0 && rawRedeemedCodes.length === 0) {
                requestCache.clear();
                const retryResponse = await api.loadCardCollection(customerId);
                if (Array.isArray(retryResponse.redeemedCodes) && retryResponse.redeemedCodes.length > 0) {
                    return await loadCardCollectionData({ showLoadingIndicator, forceRefresh: true, retryOnEmptyCodes: false });
                }
            }

            const cardIdMap = new Map();
            rawCards.forEach(card => {
                const cardIdStr = String(card.id);
                cardIdMap.set(cardIdStr, cardIdStr);
                if (!isNaN(card.id)) cardIdMap.set(String(Number(card.id)), cardIdStr);
            });

            const filteredRedeemedCodes = rawRedeemedCodes.filter(code => code && code.card_id != null && cardIdMap.has(String(code.card_id)));
            const normalizedProgress = rawProgress.map(entry => ({ ...entry, card_id: cardIdMap.get(String(entry.card_id)) || String(entry.card_id) }));
            applicationState.cardCopyCounts = buildCardCopyCounts(normalizedProgress);
            applicationState.allCards = sortCardsByCollectionStatus(rawCards, applicationState.cardCopyCounts);
            applicationState.cardsBySeriesName = groupCardsBySeries(applicationState.allCards);
            const normalizedRedeemedCodes = filteredRedeemedCodes.map(code => ({ ...code, card_id: cardIdMap.get(String(code.card_id)) || String(code.card_id) }));
            applicationState.redeemedCodesByCardId = groupRedeemedCodesByCardId(normalizedRedeemedCodes);
            applicationState.seriesCovers = rawSeriesCovers;
            applicationState.series = rawSeries;

            refreshCollectionDisplay();
        }

        let isProcessingRedemption = false;
        async function processCodeRedemption(code) {
            if (!code) {
                showStatusMessage('Please enter a code.', 'error');
                return;
            }
            if (isProcessingRedemption) {
                showStatusMessage('Please wait, processing previous code...', 'warning');
                return;
            }

            isProcessingRedemption = true;
            showStatusMessage('Processing your code…', 'warning');
            const previouslyOwnedCardIds = getOwnedCardIds();

            try {
                const result = await api.redeemCode({ customerId, customerEmail, customerFirstName, customerWhatsapp, customerProvince, code });
                showStatusMessage(result?.message || 'Card collected successfully!', result?.type || 'success');
                codeInputField.value = '';
                codeInputField.focus();
                await loadCardCollectionData({ showLoadingIndicator: false, forceRefresh: true });

                clearPendingRedeemCode(code);

                document.dispatchEvent(new CustomEvent('cv:card-collection:redeemed', {
                    detail: {
                        code,
                        message: result?.message || 'Card collected successfully!'
                    }
                }));

                if (window.joyInstance?.updateCustomerState) window.joyInstance.updateCustomerState();
                if (typeof getCustomerPoints === 'function' && customerId) getCustomerPoints(customerId, apiBaseUrl);
            } catch (error) {
                showStatusMessage(error.message || 'Failed to redeem code.', error.response?.type || 'error');
                clearPendingRedeemCode(code);
            } finally {
                isProcessingRedemption = false;
            }
        }

        function getOwnedCardIds() {
            return new Set(Object.keys(applicationState.cardCopyCounts || {}));
        }

        // EVENT HANDLERS
        function initializeUnbindHandlers() {
            modalContentArea.addEventListener('click', async function (event) {
                const unbindButton = event.target.closest('.unbind-btn-modal');
                if (!unbindButton) return;

                const codeItem = unbindButton.closest('.modal-code-item');
                const codeId = codeItem?.dataset?.codeId;
                const codeValue = codeItem?.dataset?.code;
                if (!codeId || !codeValue) return;

                if (!confirm(`Are you sure you want to unbind code ${codeValue}?`)) return;

                unbindButton.disabled = true;
                unbindButton.textContent = 'Processing...';

                try {
                    const response = await api.unbindCode({ customerId, codeId, code: codeValue });
                    alert(response?.message || 'Code unbound successfully');
                    await loadCardCollectionData({ showLoadingIndicator: false, forceRefresh: true });

                    const cardId = String(response.cardId);
                    openCardModal(applicationState.allCards.find(c => String(c.id) === cardId), applicationState.redeemedCodesByCardId[cardId] || [], applicationState.cardCopyCounts[cardId] || 0);
                } catch (error) {
                    alert(error.message || 'Failed to unbind card.');
                } finally {
                    unbindButton.disabled = false;
                    unbindButton.textContent = 'Unbind';
                }
            });
        }

        modalCloseButton?.addEventListener('click', closeCardModal);
        window.addEventListener('click', e => { if (e.target === cardModal) closeCardModal(); });
        window.addEventListener('keydown', e => { if (e.key === 'Escape') closeCardModal(); });

        collectionContainer.addEventListener('click', event => {
            const cardTile = event.target.closest('.card');
            if (!cardTile || cardTile.classList.contains('more-card')) return;

            const cardId = cardTile.dataset.cardId;
            const cardData = applicationState.allCards.find(c => String(c.id) === String(cardId));
            if (!cardData) return;

            const cardIdKey = String(cardId);
            const redeemedCodes = applicationState.redeemedCodesByCardId[cardIdKey] || [];
            const copyCount = applicationState.cardCopyCounts[cardIdKey] || 0;
            openCardModal(cardData, redeemedCodes, copyCount);
        });

        collectionContainer.addEventListener('keydown', e => { if (e.key === 'Enter') e.target.closest('.card')?.click(); });

        let hasViewedSeries = false;
        let schedulePopupCheck = () => { };

        seriesIconsContainer.addEventListener('click', event => {
            const seriesIcon = event.target.closest('.series-icon');
            if (!seriesIcon) return;
            hasViewedSeries = true;
            const seriesName = seriesIcon.dataset.series;
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('series', seriesName);
            window.history.pushState({}, '', newUrl);
            renderSingleSeriesView(seriesName);
            schedulePopupCheck();
        });

        collectionContainer.addEventListener('click', event => {
            const seeAllCard = event.target.closest('.more-card');
            if (!seeAllCard) return;
            hasViewedSeries = true;
            const seriesName = seeAllCard.dataset.series;
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('series', seriesName);
            window.history.pushState({}, '', newUrl);
            renderSingleSeriesView(seriesName);
            schedulePopupCheck();
        });

        collectionContainer.addEventListener('click', event => {
            if (event.target.id === 'back-btn') {
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('series');
                window.history.pushState({}, '', newUrl);
                renderAllSeriesView(applicationState.cardsBySeriesName);
                renderSeriesFilterIcons(applicationState.cardsBySeriesName);
            }
        });

        window.addEventListener('popstate', refreshCollectionDisplay);
        redeemButton?.addEventListener('click', () => processCodeRedemption(codeInputField.value.trim()));
        codeInputField?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); redeemButton?.click(); } });

        // INITIALIZATION
        initializeUnbindHandlers();

        const floatingBackToTopBtn = document.getElementById('floating-back-to-top');
        const scrollPopupModal = document.getElementById('scroll-popup-modal');
        const closeScrollPopupBtn = document.getElementById('close-scroll-popup-btn');
        let popupDisplayed = false;

        function toggleBackToTopButton() {
            floatingBackToTopBtn.style.display = window.scrollY > 300 ? 'block' : 'none';
        }

        floatingBackToTopBtn?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));

        function checkAndShowScrollPopup() {
            if (popupDisplayed || !scrollPopupModal || !hasViewedSeries) return;
            if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight * 0.5) {
                popupDisplayed = true;
                scrollPopupModal.style.display = 'flex';
            }
        }

        schedulePopupCheck = () => requestAnimationFrame(() => requestAnimationFrame(checkAndShowScrollPopup));
        closeScrollPopupBtn?.addEventListener('click', e => { e.preventDefault(); scrollPopupModal.style.display = 'none'; });

        let scrollAnimationFrame = null;
        window.addEventListener('scroll', () => {
            if (scrollAnimationFrame) cancelAnimationFrame(scrollAnimationFrame);
            scrollAnimationFrame = requestAnimationFrame(() => {
                toggleBackToTopButton();
                if (!popupDisplayed) checkAndShowScrollPopup();
            });
        }, { passive: true });

        const automaticRedeemCode = urlRedeemCode || readPendingRedeemCode();
        if (automaticRedeemCode) processCodeRedemption(automaticRedeemCode);

        loadCardCollectionData().catch(error => {
            collectionContainer.innerHTML = `<p style="color:red">Failed to load card collection. ${error.message}</p>`;
        });
    });
})();
